import type { GeoPoint } from '@citizen-walk/shared-types';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Browser Geolocation API ラッパー hook (REQ-4.1.6/4.1.7 相当)。
 *
 * geo-6 (docs/111 Wave 4 / docs/98 R-64): mount 時 1 回の getCurrentPosition だけだと、最初に位置情報を
 * 拒否 (または保留) したユーザーが**後からブラウザ設定で許可しても自動再取得されない**。Permissions API
 * (`navigator.permissions`) で geolocation 権限の変化を購読し、`granted` へ遷移したとき位置未確定 (status!=='ready')
 * なら自動で再取得する。Permissions API 非対応ブラウザ (一部 Safari 等) では従来どおり手動再取得のみ。
 *
 * **継続取得モード (2026-06-12、投稿画面の現在地追跡)**: `useGeolocation({ watch: true })` で
 * `watchPosition` を使い、表示中ずっと現在地を更新し続ける (`state.sample` が随時最新化)。投稿画面で
 * 「常に現在位置を取得」する要件のため。`capture()` は単発再取得用に残す (toggle の「取得」や retry)。
 * 既定 (`watch` 省略) は従来どおり単発 (getCurrentPosition) で、既存呼出は無改修。
 */
export interface GeoSample {
  location: GeoPoint;
  accuracy: number;
  capturedAt: number;
}

export type GeoState =
  | { status: 'idle'; sample: null; error: null }
  | { status: 'pending'; sample: null; error: null }
  | { status: 'ready'; sample: GeoSample; error: null }
  | { status: 'error'; sample: null; error: GeolocationPositionError | Error };

const DEFAULT_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 0,
};

function geolocationAvailable(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.geolocation;
}

export function useGeolocation(opts?: { watch?: boolean }): {
  state: GeoState;
  capture: () => void;
  reset: () => void;
} {
  const watch = opts?.watch ?? false;
  // watch モードは取得まで時間がかかるため、初期から 'pending' を出して「取得中」UI を表示する
  // (effect 内同期 setState を避けるための lazy 初期化、react-hooks/set-state-in-effect 回避)。
  const [state, setState] = useState<GeoState>(() =>
    watch && geolocationAvailable()
      ? { status: 'pending', sample: null, error: null }
      : { status: 'idle', sample: null, error: null },
  );

  const capture = useCallback(() => {
    if (!geolocationAvailable()) {
      setState({
        status: 'error',
        sample: null,
        error: new Error('Geolocation API がこのブラウザで利用できません'),
      });
      return;
    }
    setState({ status: 'pending', sample: null, error: null });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setState({
          status: 'ready',
          sample: {
            location: { latitude: pos.coords.latitude, longitude: pos.coords.longitude },
            accuracy: pos.coords.accuracy,
            capturedAt: pos.timestamp,
          },
          error: null,
        });
      },
      (err) => setState({ status: 'error', sample: null, error: err }),
      DEFAULT_OPTIONS,
    );
  }, []);

  const reset = useCallback(() => {
    setState({ status: 'idle', sample: null, error: null });
  }, []);

  // status を ref で参照 (効果の再実行 = listener 再登録を避ける)。ref 更新は effect で行う。
  const statusRef = useRef(state.status);
  useEffect(() => {
    statusRef.current = state.status;
  }, [state.status]);

  // 継続取得 (watch=true): watchPosition で位置を更新し続ける。一過性エラーは ready を温存。
  // watchEpoch を増やすと watch を再開する (権限後付与時の自動再開に使う)。
  const [watchEpoch, setWatchEpoch] = useState(0);
  useEffect(() => {
    if (!watch || !geolocationAvailable()) return;
    const id = navigator.geolocation.watchPosition(
      (pos) =>
        setState({
          status: 'ready',
          sample: {
            location: { latitude: pos.coords.latitude, longitude: pos.coords.longitude },
            accuracy: pos.coords.accuracy,
            capturedAt: pos.timestamp,
          },
          error: null,
        }),
      (err) =>
        // 既に fix 済 (ready) のときは一過性エラーで最後の位置を捨てない。
        setState((s) => (s.status === 'ready' ? s : { status: 'error', sample: null, error: err })),
      DEFAULT_OPTIONS,
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [watch, watchEpoch]);

  // geo-6: 位置許可を後から付与した場合の自動再取得 (Permissions API watch)。
  // watch モードでは watch を再開 (watchEpoch++)、単発モードでは capture() を呼ぶ。
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return;
    let cancelled = false;
    let perm: PermissionStatus | null = null;
    const onChange = () => {
      if (cancelled || perm?.state !== 'granted' || statusRef.current === 'ready') return;
      if (watch) {
        setState({ status: 'pending', sample: null, error: null });
        setWatchEpoch((e) => e + 1);
      } else {
        capture();
      }
    };
    navigator.permissions
      .query({ name: 'geolocation' as PermissionName })
      .then((p) => {
        if (cancelled) return;
        perm = p;
        p.addEventListener('change', onChange);
      })
      .catch(() => {
        /* Permissions API 不可ブラウザは手動再取得のみ */
      });
    return () => {
      cancelled = true;
      perm?.removeEventListener('change', onChange);
    };
  }, [capture, watch]);

  return { state, capture, reset };
}
